import React, { useState, useEffect, useContext } from "react";
import locationIconLight from "../../../Assets/Images/job-location-icon-light.svg";
import locationIconDark from "../../../Assets/Images/job-location-icon-dark.svg";
import Button from "../../Button/Button";
import CustomTooltip from "../../../Common/Tooltip/Tooltip";
import {
  applyEllipsis,
  toCapitalize,
} from "../../Common-Function/CommonFunctions";
import ExperienceIconLight from "../../../Assets/Images/exp-job-icon-light.svg";
import ExperienceIconDark from "../../../Assets/Images/exp-job-icon-dark.svg";
import { DynamicContext } from "../../../Context/DynamicContext";

const PublicJobCard = ({
  title,
  location,
  minYearsExperience,
  maxYearsExperience,
  experience,
  jobtype,
  client,
  jobRole,
  client_contact_person,
  actionText,
  viewJobEventHandler = () => {},
  applyJobEventHandler = () => {},
  viewJob = "View Job",
  applyJob = "Apply Job",
  isSelectMode = false,
  isSelected = false,
  handleCardSelect = () => {},
  matchPercentage,
  showTitleTooltip = true,
}) => {
  const { isDarkMode } = useContext(DynamicContext);
  
  const [ellipsisLength, setEllipsisLength] = useState(() => {
    if (window.innerWidth > 2290) return 80;
    if (window.innerWidth <= 2290 && window.innerWidth >= 1660) return 50;
    if (window.innerWidth <= 1660 && window.innerWidth >= 425) return 30;
    if (window.innerWidth < 950) return 20;
    return 28;
  });

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 2290) {
        setEllipsisLength(80);
      } else if (window.innerWidth <= 2290 && window.innerWidth >= 1660) {
        setEllipsisLength(50);
      } else if (window.innerWidth <= 1660 && window.innerWidth >= 425) {
        setEllipsisLength(30);
      } else if (window.innerWidth < 950) {
        setEllipsisLength(20);
      } else {
        setEllipsisLength(28);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const getMatchFitLabelAndColor = (percentage) => {
    const val = parseFloat(percentage);
    if (isNaN(val)) {
      return { label: "N/A", color: "var(--header__color)" };
    }
    if (val >= 90) return { label: "Top Fit", color: "#22C55E" };
    if (val >= 66) return { label: "Good Fit", color: "#3B82F6" };
    if (val >= 33) return { label: "Partial Fit", color: "#F59E0B" };
    return { label: "Unlikely Fit", color: "#EF4444" };
  };

  const hasValidExperience = (value) =>
    value !== null &&
    value !== undefined &&
    value !== "" &&
    Number(value) > 0;

  const getExperienceLabel = () => {
    if (hasValidExperience(minYearsExperience)) {
      return hasValidExperience(maxYearsExperience)
        ? `${minYearsExperience} - ${maxYearsExperience} Years`
        : `${minYearsExperience} Years`;
    }

    if (hasValidExperience(maxYearsExperience)) {
      return `${maxYearsExperience} Years`;
    }

    if (hasValidExperience(experience)) {
      return `${experience} Years`;
    }

    return "N/A";
  };

  const titleContent = (
    <div className="job-header-icon-container" style={{ minWidth: 0 }}>
      {isSelectMode && (
        <div
          className="selection-checkbox"
          onClick={(e) => e.stopPropagation()}
          style={{ marginRight: "10px" }}
        >
          <input
            type="checkbox"
            checked={isSelected}
            onChange={(e) => {
              e.stopPropagation();
              handleCardSelect();
            }}
          />
        </div>
      )}
      <div>
        <h3 className="jobs-card-title">
          {applyEllipsis(jobRole || "N/A", ellipsisLength)}
        </h3>
        <div className="job-headers-spans">
          <span className="headers-client-span">
            {toCapitalize(client || "N/A")}
            {client_contact_person
              ? ` ( ${toCapitalize(client_contact_person)} )`
              : ""}
          </span>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <div
        className="jobs-card-container"
        style={{
          borderColor:
            matchPercentage !== undefined && matchPercentage !== null
              ? getMatchFitLabelAndColor(matchPercentage).color
              : "var(--card__border)",
          borderWidth: "1px",
          borderStyle: "solid",
        }}
      >
        <div className="jobs-card-header">
          {showTitleTooltip ? (
            <CustomTooltip title={title || "N/A"} arrow placement="top">
              {titleContent}
            </CustomTooltip>
          ) : (
            titleContent
          )}
          <div
            style={{
              display: "grid",
              gap: "15px",
            }}
          >
            {matchPercentage !== undefined && matchPercentage !== null && (
              <div
                className="job-match-percentage"
                style={{
                  position: "absolute",
                  top: "0",
                  right: "0",
                  zIndex: 2,
                }}
              >
                <span
                  style={{
                    backgroundColor:
                      getMatchFitLabelAndColor(matchPercentage).color,
                    color: "#fff",
                    padding: "6px 12px",
                    borderTopRightRadius: "12px",
                    borderBottomLeftRadius: "12px",
                    fontSize: "12px",
                    fontWeight: "500",
                    textTransform: "capitalize",
                    display: "inline-block",
                    minWidth: "10px",
                    textAlign: "center",
                  }}
                >
                  {getMatchFitLabelAndColor(matchPercentage).label}
                </span>
              </div>
            )}
          </div>
        </div>
        <div className="jobs-card-info">
          <div className="jobs-card-info-details">
            <img
              src={isDarkMode ? ExperienceIconDark : ExperienceIconLight}
              alt="experience-icon"
            />
            <span>{getExperienceLabel()}</span>
          </div>
          <div className="jobs-card-info-details">
            <img
              src={isDarkMode ? locationIconDark : locationIconLight}
              alt="location-icon"
            />
            <span>
              {applyEllipsis(toCapitalize(location), 25)}{" "}
              {jobtype && (
                <span>( {toCapitalize(jobtype || "N/A")} )</span>
              )}{" "}
            </span>
          </div>
        </div>
        {actionText && (
          <div className="jobs-card-action">
            <Button
              label={viewJob}
              event={viewJobEventHandler}
              backgroundColor="var(--body__background)"
              color="#6860EE"
              border="1px solid #6860EE"
              style={{ padding: "8px 12px" }}
              className="view-job"
            />
            <Button
              label={applyJob}
              event={applyJobEventHandler}
              backgroundColor="#6860EE"
              color="#FFFFFF"
              border="1px solid #6860EE"
              style={{ padding: "8px 12px" }}
              className="apply-job"
            />
          </div>
        )}
      </div>
    </>
  );
};

export default PublicJobCard;
